
import React, { useEffect, Fragment, useState } from "react";
import styled from "styled-components";
import { useForm, Controller } from 'react-hook-form';
import DialogModal from "components/Modal/Dialog";
import { isEmpty } from "lodash";
import EmpEditHeader from "../../editHeader";
import SelectDropdown from "components/Dropdowns/SelectDropdown";
import SelectWithAdd from "components/Dropdowns/SelectWithAdd";
import EnumDropdown from "components/Dropdowns/EnumDropdown";
import TextArea from "components/TextArea";
import InputWithSelect from "components/Dropdowns/InputWithSelect";
import {
  getPaymentPeriod,
  getPaymentTypes,
  createPaymentType,
  getPaymentSchedule,
  createPaymentSchedule,
  getCompensationChangeReason,
  createCompensationChangeReason,
  getEnum,
  getCurrencies,
} from 'services'
import { useToasts } from "react-toast-notifications";
import DatePicker from "components/DatePickers/DatePicker";
import { utcToZonedTime } from 'date-fns-tz';

const CompensationInformationEdit = (props: any) => {
  const { handleSubmit, setValue, setError, control, formState: { errors } } = useForm({
    defaultValues: {
      effective_date: new Date(),
      pay_amount: { inputValue: '', selectValue: '' },
      payment_period: '',
      payment_type: null,
      payment_schedule: null,
      overtime_status: '',
      compensation_change_reason: null,
      comment: ''
    }
  });

  const {
    user,
    jobData,
    currencies,
    chosenItem,
    updateMode,
    isTermination,
    loadingRequest,
    disabled = false,
  } = props;

  const [overtimeStatusData, setOvertimeStatusData] = useState([]);
  const { addToast } = useToasts();

  const onSubmit = (data: any) => {
    props.onSubmit(data);
  };

  useEffect(() => {
    getEnum(25, 1, 'Enum::OvertimeStatus').then(res => setOvertimeStatusData(res.data.list));
  }, []);

  useEffect(() => {
    if (isTermination && updateMode) {
      setValue('effective_date', new Date());
    } else {
      if (chosenItem) {
        setValue('effective_date', updateMode ? new Date() : utcToZonedTime(new Date(chosenItem?.effective_date), 'UTC'));
        setValue('pay_amount', { inputValue: (+chosenItem?.pay_amount).toFixed(2), selectValue: chosenItem?.currency });
        setValue('payment_period', chosenItem?.payment_period ?? null);
        setValue('payment_type', chosenItem?.payment_type ?? null)
        setValue('payment_schedule', chosenItem?.payment_schedule ?? null)
        setValue('overtime_status', chosenItem?.overtime_status?.id ?? '')
        setValue('compensation_change_reason', updateMode ? null : chosenItem?.compensation_change_reason || null)
        setValue('comment', updateMode ? '' : chosenItem?.comment);
      }
    }
  }, [user, chosenItem, isTermination]);

  useEffect(() => {
    if (currencies && ((!chosenItem?.currency || isTermination) && updateMode)) {
      setValue('pay_amount', {
        inputValue: ((!chosenItem?.currency || isTermination) && updateMode) ? "" : chosenItem?.pay_amount,
        selectValue: currencies[0]
      });
    }
  }, [currencies]);

  useEffect(() => {
    if (props.formErrors) {
      props.formErrors.forEach((item: any) => {
        if (item.field && item.field !== 'base') {
          setError(item.field, { type: 'string', message: item.message })
        } else {
          addToast(item.message, {
            appearance: 'error',
            autoDismiss: true,
          });
        }
      })
    }
  }, [props.formErrors]);

  const onError = (err: any) => {
    if (err) {
      addToast(<ToastContentContainer>
        <b>There were some errors with your form.</b> Please Fix The Highlighted Fields Below
      </ToastContentContainer>, {
        appearance: 'error',
        autoDismiss: true,
        placement: 'top-center'
      });
    }
  };

  return (
    <DialogModal
      open={props.isOpen}
      onClose={() => props.onModalClose()}
      title={updateMode ? 'Update Compensation' : 'Edit Compensation'}
      hideActionButton={disabled ? true : false}
      withButtons
      cancelButtonText='CANCEL'
      actionButtonText='SAVE'
      actionButton={handleSubmit(onSubmit, onError)}
      actionLoading={loadingRequest}
      fullWidth
      nominalHeader={
        <EmpEditHeader
          employeeName={`${user.first_name} ${user.last_name}`}
          avatarUuid={user.uuid}
          employeeId={user.id}
          jobData={jobData}
        />
      }
    >
      <Fragment>
        <Wrapper>
          <div className='body'>
            <div className='compensation-section'>
              <div className='input-item' style={{ width: 200 }}>
                <Controller
                  name="effective_date"
                  control={control}
                  rules={{ validate: (value: any) => value === null ? 'Please enter a valid date' : value !== '' || 'Effective Date is required' }}
                  render={({ field: { onChange, value } }) => (
                    <DatePicker
                      required
                      selected={value}
                      onChange={onChange}
                      label='Effective Date'
                      errorText={errors.effective_date ? errors.effective_date.message : ''}
                      disabled={disabled}
                    />
                  )}
                />
              </div>

              <div className='input-item'>
                <label>Pay Rate<sup>*</sup></label>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Controller
                      name="pay_amount"
                      control={control}
                      rules={{ validate: value => !isEmpty(value.inputValue) }}
                      render={({ field: { onChange, value } }) => (
                        <InputWithSelect
                          errorText={errors.pay_amount ? "Pay rate is required" : ""}
                          validateToDecimal
                          onChange={onChange}
                          value={value}
                          selectProps={{
                            loadRemoteData: () => getCurrencies(55, 1),
                            disabled: disabled
                          }}
                          inputProps={{
                            disabled: disabled
                          }}
                        />
                      )}
                    />
                  </div>

                  <div style={{ display: 'flex', marginLeft: 20 }}>
                    <span style={{ marginRight: 5, marginTop: 13 }}>Per<sup style={{ color: '#C54343' }}>*</sup></span>
                    <Controller
                      name="payment_period"
                      control={control}
                      rules={{
                        required: 'Pay Period is required'
                      }}
                      render={({ field: { onChange, value } }) => (
                        <SelectDropdown
                          inputPlaceholder='Select Period'
                          fullWidth={false}
                          sx={{ width: 170 }}
                          size="small"
                          onChange={(_event: React.SyntheticEvent<Element, Event>, newValue: any) => onChange(newValue)}
                          errorText={errors.payment_period ? errors.payment_period.message : ''}
                          value={value}
                          loadRemoteData={() => getPaymentPeriod(25, 1)}
                          disabled={disabled}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className='input-item'>
                <label>Pay Type</label>
                <Controller
                  name="payment_type"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <SelectWithAdd
                      name={'payment_type'}
                      inputPlaceholder='Select Pay Type'
                      inputValue={value}
                      loadRemoteData={() => getPaymentTypes(25, 1)}
                      createRequest={createPaymentType}
                      onChange={onChange}
                      disabled={disabled}
                    />
                  )}
                />
              </div>

              <div className='input-item'>
                <label>Pay Schedule</label>
                <Controller
                  name="payment_schedule"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <SelectWithAdd
                      name={'payment_schedule'}
                      inputPlaceholder='Select Pay Schedule'
                      inputValue={value}
                      loadRemoteData={() => getPaymentSchedule(25, 1)}
                      createRequest={createPaymentSchedule}
                      onChange={onChange}
                      disabled={disabled}
                    />
                  )}
                />
              </div>

              <div className='input-item'>
                <label>Overtime Status</label>
                <Controller
                  name="overtime_status"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <EnumDropdown
                      placeholder='Select Overtime Status'
                      onChange={onChange}
                      value={value}
                      options={overtimeStatusData}
                      disabled={disabled}
                    />
                  )}
                />
              </div>

              <div className='input-item'>
                <label>Change Reason</label>
                <Controller
                  name="compensation_change_reason"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <SelectWithAdd
                      name={'compensation_change_reason'}
                      inputPlaceholder='Select Change Reason'
                      inputValue={value}
                      loadRemoteData={() => getCompensationChangeReason(25, 1)}
                      createRequest={createCompensationChangeReason}
                      onChange={onChange}
                      disabled={disabled}
                    />
                  )}
                />
              </div>

              <div className='input-item' style={{ marginTop: -5, marginBottom: 0 }}>
                <Controller
                  name="comment"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <TextArea
                      onChange={(event: any) => { onChange(event.target.value) }}
                      label={'Comment'}
                      defaultValue={value}
                      maxRows={5}
                      disabled={disabled}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </Wrapper>
      </Fragment>
    </DialogModal >
  );
};

export default CompensationInformationEdit;

const ToastContentContainer = styled.div`
    & > b {
        font-family: 'Aspira Demi';
    }
`;

const Wrapper = styled.div`
.body{
  .input-item{
    max-width: 416px;
    margin-bottom: 16px;
    label {
        display: block;
        margin-bottom: 5px;
        & > sup {
          color: #C54343;
        }
    }
  }
}
`;